import { useState, useMemo, useEffect, type ReactNode } from "react";
import { useLocation } from "wouter";
import { formatCents } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { ArrowUpDown, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import type { ClaimFinancialRow } from "@/api/generated/client/models";

type SortField =
    | "insuredName"
    | "claimNumber"
    | "createdAt"
    | "rcv"
    | "depreciation"
    | "acv"
    | "deductible"
    | "netClaim"
    | "recoverableDepreciation"
    | "nonRecoverableDepreciation"
    | "netWithRecoverable"
    | "expectedFee"
    | "paymentsCollected"
    | "feesCollected";
type SortDir = "asc" | "desc";

interface FinancialsTableProps {
    data: ClaimFinancialRow[];
    /** Optional YYYY-MM filter to show only claims from a specific month */
    monthFilter?: string | null;
    onClearMonthFilter?: () => void;
}

function fmtCents(cents: number): string {
    if (cents === 0) return "—";
    return formatCents(cents);
}

/**
 * Resolve the effective expected fee for a claim row.
 * The backend repository already resolves flat-fee vs percentage logic
 * into expectedFeeCents, so this simply returns that pre-computed value.
 */
function getEffectiveExpectedFee(item: ClaimFinancialRow): number {
    return item.expectedFeeCents;
}

function fmtFeeInfo(item: ClaimFinancialRow): string {
    if (!item.feeType) return "—";
    if (item.feeType === "percentage") return `${item.feePercentage ?? 0}%`;
    if (item.feeType === "flat_fee") return "Flat";
    return "—";
}

const PAGE_SIZE = 50;

export function FinancialsTable({ data, monthFilter, onClearMonthFilter }: FinancialsTableProps) {
    const [, setLocation] = useLocation();
    const [sortField, setSortField] = useState<SortField>("createdAt");
    const [sortDir, setSortDir] = useState<SortDir>("desc");
    const [page, setPage] = useState(1);

    // Apply month filter if set
    const filteredData = useMemo(() => {
        if (!monthFilter) return data;
        return data.filter((item) => item.createdAt.startsWith(monthFilter));
    }, [data, monthFilter]);

    const sorted = useMemo(() => {
        const getVal = (item: ClaimFinancialRow): string | number => {
            switch (sortField) {
                case "insuredName": return item.insuredName.toLowerCase();
                case "claimNumber": return item.claimNumber.toLowerCase();
                case "createdAt": return item.createdAt;
                case "rcv": return item.rcvCents;
                case "depreciation": return item.depreciationCents;
                case "acv": return item.acvCents;
                case "deductible": return item.deductibleCents;
                case "netClaim": return item.netClaimCents;
                case "recoverableDepreciation": return item.recoverableDepreciationCents;
                case "nonRecoverableDepreciation": return item.nonRecoverableDepreciationCents;
                case "netWithRecoverable": return item.netWithRecoverableCents;
                case "expectedFee": return getEffectiveExpectedFee(item);
                case "paymentsCollected": return item.totalPaymentsCents;
                case "feesCollected": return item.actualFeeCents;
            }
        };

        return [...filteredData].sort((a, b) => {
            const aVal = getVal(a);
            const bVal = getVal(b);
            if (typeof aVal === "string") {
                return sortDir === "asc"
                    ? aVal.localeCompare(bVal as string)
                    : (bVal as string).localeCompare(aVal);
            }
            return sortDir === "asc"
                ? (aVal as number) - (bVal as number)
                : (bVal as number) - (aVal as number);
        });
    }, [filteredData, sortField, sortDir]);

    const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
    const paginated = useMemo(
        () => sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [sorted, page],
    );

    // Clamp page when data shrinks (e.g. refetch returns fewer rows or filter empties results)
    useEffect(() => {
        if (totalPages === 0 && page !== 1) {
            setPage(1);
        } else if (totalPages > 0 && page > totalPages) {
            setPage(totalPages);
        }
    }, [totalPages, page]);

    // Reset page when data or sort changes
    const handleSort = (field: SortField) => {
        setPage(1);
        if (sortField === field) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortDir("asc");
        }
    };

    function SortButton({ field, children }: { field: SortField; children: ReactNode }) {
        return (
            <Button
                variant="ghost"
                size="sm"
                className="-ml-3 h-8 data-[state=open]:bg-accent"
                onClick={() => handleSort(field)}
            >
                {children}
                <ArrowUpDown className="ml-2 h-3.5 w-3.5" />
            </Button>
        );
    }

    if (filteredData.length === 0) {
        return (
            <>
            {monthFilter && onClearMonthFilter && (
                <div className="mb-2 flex items-center gap-2 max-sm:flex-wrap">
                    <Badge variant="secondary" className="gap-1">
                        Filtered: {monthFilter}
                        <button
                            onClick={onClearMonthFilter}
                            className="ml-1 hover:text-destructive"
                            aria-label="Clear month filter"
                        >
                            ✕
                        </button>
                    </Badge>
                </div>
            )}
            <div className="text-center py-12 text-muted-foreground border rounded-md bg-card">
                {monthFilter ? `No claims found for ${monthFilter}.` : "No claims found matching your criteria."}
            </div>
            </>
        );
    }

    return (
        <>
        {monthFilter && onClearMonthFilter && (
            <div className="mb-2 flex items-center gap-2 max-sm:flex-wrap">
                <Badge variant="secondary" className="gap-1">
                    Filtered: {monthFilter}
                    <button
                        onClick={onClearMonthFilter}
                        className="ml-1 hover:text-destructive"
                        aria-label="Clear month filter"
                    >
                        ✕
                    </button>
                </Badge>
            </div>
        )}
        <div className="rounded-md border bg-card text-card-foreground overflow-x-auto">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="sticky left-0 bg-card z-10">
                            <SortButton field="insuredName">Insured</SortButton>
                        </TableHead>
                        <TableHead>
                            <SortButton field="claimNumber">Claim #</SortButton>
                        </TableHead>
                        <TableHead>
                            <SortButton field="createdAt">Created</SortButton>
                        </TableHead>
                        {/* Estimate Breakdown — same order as claim detail */}
                        <TableHead className="text-right">
                            <SortButton field="rcv">RCV</SortButton>
                        </TableHead>
                        <TableHead className="text-right">
                            <SortButton field="depreciation">Depr.</SortButton>
                        </TableHead>
                        <TableHead className="text-right">
                            <SortButton field="acv">ACV</SortButton>
                        </TableHead>
                        <TableHead className="text-right">
                            <SortButton field="deductible">Deductible</SortButton>
                        </TableHead>
                        <TableHead className="text-right">
                            <SortButton field="netClaim">Net Claim</SortButton>
                        </TableHead>
                        <TableHead className="text-right">
                            <SortButton field="recoverableDepreciation">Rec. Depr.</SortButton>
                        </TableHead>
                        <TableHead className="text-right">
                            <SortButton field="nonRecoverableDepreciation">Non-Rec.</SortButton>
                        </TableHead>
                        <TableHead className="text-right">
                            <SortButton field="netWithRecoverable">Net w/ Rec.</SortButton>
                        </TableHead>
                        {/* Fees */}
                        <TableHead className="text-center">Fee</TableHead>
                        <TableHead className="text-right">
                            <SortButton field="expectedFee">Exp. Fee</SortButton>
                        </TableHead>
                        {/* Collections */}
                        <TableHead className="text-right">
                            <SortButton field="paymentsCollected">Payments</SortButton>
                        </TableHead>
                        <TableHead className="text-right">
                            <SortButton field="feesCollected">Fees Coll.</SortButton>
                        </TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {paginated.map((item) => (
                        <TableRow
                            key={item.claimId}
                            className="group cursor-pointer hover:bg-muted/50"
                            tabIndex={0}
                            onClick={() => setLocation(`/claims/${item.claimId}`)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setLocation(`/claims/${item.claimId}`);
                                }
                            }}
                        >
                            <TableCell className="font-medium sticky left-0 bg-card z-10">
                                {item.insuredName}
                            </TableCell>
                            <TableCell className="text-muted-foreground whitespace-nowrap">
                                {item.claimNumber}
                            </TableCell>
                            <TableCell className="text-muted-foreground whitespace-nowrap">
                                {new Date(item.createdAt).toLocaleDateString()}
                            </TableCell>
                            {/* Estimate Breakdown */}
                            <TableCell className="text-right font-mono">
                                {fmtCents(item.rcvCents)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-red-500">
                                {fmtCents(item.depreciationCents)}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold">
                                {fmtCents(item.acvCents)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-orange-500">
                                {fmtCents(item.deductibleCents)}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold">
                                {fmtCents(item.netClaimCents)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                                {fmtCents(item.recoverableDepreciationCents)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-red-400">
                                {fmtCents(item.nonRecoverableDepreciationCents)}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold text-green-600 dark:text-green-400">
                                {fmtCents(item.netWithRecoverableCents)}
                            </TableCell>
                            {/* Fees */}
                            <TableCell className="text-center whitespace-nowrap">
                                <Badge variant="outline" className="text-xs">
                                    {fmtFeeInfo(item)}
                                </Badge>
                            </TableCell>
                            <TableCell className="text-right font-mono">
                                {fmtCents(getEffectiveExpectedFee(item))}
                            </TableCell>
                            {/* Collections */}
                            <TableCell className="text-right font-mono font-medium text-emerald-600 dark:text-emerald-400">
                                {item.totalPaymentsCents > 0 ? formatCents(item.totalPaymentsCents) : "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono font-medium text-blue-600 dark:text-blue-400">
                                {item.actualFeeCents > 0 ? formatCents(item.actualFeeCents) : "—"}
                            </TableCell>
                            <TableCell>
                                <a
                                    href={`/claims/${item.claimId}`}
                                    aria-label={`Open claim ${item.claimNumber}`}
                                    className="inline-flex items-center justify-center h-8 w-8 rounded-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-accent hover:text-accent-foreground"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setLocation(`/claims/${item.claimId}`);
                                    }}
                                >
                                    <ExternalLink className="h-4 w-4" />
                                </a>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>

        {totalPages > 1 && (
            <div className="flex flex-col gap-3 border-t pt-4 md:flex-row md:items-center md:justify-between md:gap-0">
                <p className="text-sm text-muted-foreground">
                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of {sorted.length} claims
                </p>
                <div className="flex w-full items-center justify-between gap-2 md:w-auto md:justify-start">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                    >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                    </Button>
                    <span className="text-sm text-muted-foreground px-2">
                        Page {page} of {totalPages}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                    >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                </div>
            </div>
        )}
        </>
    );
}
